import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import ConfettiExplosion from 'react-confetti-explosion';

/**
 * Animated favourite heart button.
 *
 * On toggle-on the heart pops and a filled overlay springs in from its centre
 * (the "fill itself" effect), followed by a small confetti burst. On toggle-off
 * it simply deflates. Favourite state stays controlled by the parent via
 * `active` / `onToggle`, matching the existing local-state pattern in the cards.
 */

const HEART_PATH =
    'M12 21s-7-4.35-9.5-8.5C1 9 2.5 5.5 6 5.5c2 0 3.2 1.2 4 2.3.8-1.1 2-2.3 4-2.3 3.5 0 5 3.5 3.5 7C19 16.65 12 21 12 21z';

// Small, warm burst tuned to the secondary/gold palette.
// The library portals particles to <body>; `zIndex` lifts them above modals
// (event preview modal is z-[130]) so the burst is never hidden behind them.
const CONFETTI_PROPS = {
    particleCount: 24,
    particleSize: 8,
    duration: 1800,
    force: 0.4,
    width: 200,
    height: '110vh',
    colors: ['#f5c451', '#facc15', '#fde68a', '#fbbf24', '#ffffff'],
    zIndex: 9999,
};

export default function FavoriteHeart({
    active = false,
    onToggle,
    size = 18,
    buttonClassName = '',
    ariaLabel = 'favourite',
}) {
    // `burstKey` remounts the explosion so rapid re-favouriting replays it.
    const [burstKey, setBurstKey] = useState(0);
    const [bursting, setBursting] = useState(false);

    const handleClick = useCallback(
        (e) => {
            // Fire confetti only on the off -> on transition.
            if (!active) {
                setBurstKey((k) => k + 1);
                setBursting(true);
            }
            onToggle?.(e);
        },
        [active, onToggle],
    );

    return (
        <motion.button
            type="button"
            onClick={handleClick}
            aria-label={ariaLabel}
            aria-pressed={active}
            whileTap={{ scale: 0.85 }}
            // NOTE: do not force `relative` here, callers may pass `absolute`
            // (e.g. a heart pinned to a card cover). Tailwind's `.relative` is
            // ordered after `.absolute`, so prepending it would silently win and
            // knock the button out of position. The confetti span below anchors
            // to whatever positioning the button already has.
            className={buttonClassName}
        >
            {bursting && (
                <span className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                    <ConfettiExplosion
                        key={burstKey}
                        {...CONFETTI_PROPS}
                        onComplete={() => setBursting(false)}
                    />
                </span>
            )}

            <motion.svg
                viewBox="0 0 24 24"
                style={{ width: size, height: size }}
                animate={active ? { scale: [1, 0.8, 1.25, 1] } : { scale: 1 }}
                transition={{ duration: 0.45, ease: 'easeOut', times: [0, 0.2, 0.6, 1] }}
                className={active ? 'text-secondary-400' : 'text-white'}
            >
                {/* Always-visible outline */}
                <path
                    d={HEART_PATH}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                {/* Filled heart that springs in from its own centre */}
                <motion.path
                    d={HEART_PATH}
                    fill="currentColor"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    initial={false}
                    animate={{ scale: active ? 1 : 0, opacity: active ? 1 : 0 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 15 }}
                    style={{ transformOrigin: 'center', transformBox: 'fill-box' }}
                />
            </motion.svg>
        </motion.button>
    );
}
